import {
  BlotterTable,
  BlotterTableFilters,
  Box,
  Button,
  ButtonVariants,
  FormControlSizes,
  HStack,
  IconName,
  useBlotterObservables,
  useBlotterTable,
  type CareOrder,
  type SizeModeEnum,
} from '@talos/kyoko';
import type { RowSelectionOptions } from 'ag-grid-community';
import { useMemo } from 'react';
import { getCompatibleCareOrders } from './getCompatibleCareOrders';
import type { SelectableCareOrder } from './SelectableCareOrder';
import { useCareOrderNettingPreviewColumns } from './useCareOrderNettingPreviewColumns';
import { useCareOrderNettingPreviewSelection } from './useCareOrderNettingPreviewSelection';
import type { CareOrderSelectionChange } from './useSelectableCareOrders';

const compatibleCareOrdersRowSelection = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  selectAll: 'filtered',
  enableClickSelection: true,
  enableSelectionWithoutKeys: true,
  copySelectedRows: true,
} as const satisfies RowSelectionOptions<SelectableCareOrder>;

interface CompatibleCareOrdersBlotterProps {
  isModificationMode?: boolean;
  portalId?: string;
  showAllDecimals: boolean;
  allCareOrders: SelectableCareOrder[];
  referenceCareOrder?: CareOrder;
  sizeMode: SizeModeEnum;
  onSelectionChanged: (selectionChanges: CareOrderSelectionChange[]) => void;
  onClose: () => void;
}

export function CompatibleCareOrdersBlotter({
  isModificationMode = false,
  portalId,
  showAllDecimals,
  allCareOrders,
  referenceCareOrder,
  sizeMode,
  onSelectionChanged,
  onClose,
}: CompatibleCareOrdersBlotterProps) {
  const displayedCareOrders = useMemo(
    () => getCompatibleCareOrders({ allCareOrders, isModificationMode, referenceCareOrder, sizeMode }),
    [allCareOrders, isModificationMode, referenceCareOrder, sizeMode]
  );

  const {
    selectedRows,
    updateCareOrderSelection: addCareOrder,
    updateSelectedCareOrdersSelection: addSelectedCareOrders,
    handleSelectionChanged,
  } = useCareOrderNettingPreviewSelection({
    action: 'add',
    onSelectionChanged,
  });

  const columns = useCareOrderNettingPreviewColumns({
    actionColumn: {
      type: 'add',
      onClick: addCareOrder,
    },
  });

  const { dataObservable, errorObservable } = useBlotterObservables({
    data: displayedCareOrders,
  });
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<SelectableCareOrder>({
    dataObservable,
    errorObservable,
    rowID: 'OrderID' satisfies keyof SelectableCareOrder,
    columns,
    /* We add and remove rows from this blotter based on user actions. Scroll got messed up with 
    async transactions applying the state update. Going to synchronous updates fixed it. */
    applyTransactionsSynchronously: true,
    gridOptions: {
      rowSelection: compatibleCareOrdersRowSelection,
      onSelectionChanged: handleSelectionChanged,
    },
    settings: blotterSettings,
  });

  return (
    <Box h="100%" data-testid="care-order-netting-preview-compatible-care-orders">
      <BlotterTableFilters
        portalId={portalId}
        {...blotterTable.blotterTableFiltersProps}
        showFilterBuilder={false}
        showDateRangePicker={false}
        suffix={
          <HStack gap="spacing6">
            <Button
              size={FormControlSizes.Small}
              onClick={addSelectedCareOrders}
              variant={ButtonVariants.Primary}
              startIcon={IconName.Plus}
              disabled={selectedRows.length === 0}
              data-testid="care-order-netting-add-selected-button"
            >
              Add Selected
            </Button>
            <Button
              size={FormControlSizes.Small}
              onClick={onClose}
              data-testid="care-order-netting-close-compatible-care-orders-button"
            >
              Close
            </Button>
          </HStack>
        }
      />
      <Box h="100%">
        <BlotterTable {...blotterTable} />
      </Box>
    </Box>
  );
}
